import { Controller } from "@hotwired/stimulus"

// Debounces input on a data-table search form, then submits to refresh
// the surrounding turbo-frame. Before submitting, syncs hidden sort/dir
// inputs from the current URL — column-header clicks update the URL via
// data-turbo-action="advance" but don't touch the form's fields.
//
// When `prefixValue` is set, all params are namespaced as
// `<prefix>_q`, `<prefix>_sort`, `<prefix>_dir` so multiple tables can
// live on the same page without colliding.
export default class extends Controller {
  static targets = ["input", "clear", "sortSelect"]
  static values  = {
    delay:  { type: Number, default: 300 },
    prefix: { type: String, default: "" }
  }

  connect() {
    this.timer = null
    this.toggleClear()
  }

  disconnect() { clearTimeout(this.timer) }

  submit() {
    this.toggleClear()
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.flush(), this.delayValue)
  }

  clear() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
    }
    this.toggleClear()
    clearTimeout(this.timer)
    this.flush()
  }

  sortChanged(event) {
    const [col, dir] = (event.target.value || "").split("|")
    this.setHidden(this.paramName("sort"), col)
    this.setHidden(this.paramName("dir"),  dir)
    clearTimeout(this.timer)
    this.element.requestSubmit()
  }

  flush() {
    this.syncFromUrl()
    this.element.requestSubmit()
  }

  toggleClear() {
    if (!this.hasClearTarget || !this.hasInputTarget) return
    this.clearTarget.hidden = this.inputTarget.value.length === 0
  }

  syncFromUrl() {
    const params = new URLSearchParams(window.location.search)
    this.setHidden(this.paramName("sort"), params.get(this.paramName("sort")))
    this.setHidden(this.paramName("dir"),  params.get(this.paramName("dir")))
  }

  setHidden(name, value) {
    let input = this.element.querySelector(`input[type="hidden"][name="${name}"]`)
    if (value == null || value === "") {
      if (input) input.remove()
      return
    }
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      this.element.appendChild(input)
    }
    input.value = value
  }

  paramName(base) {
    return this.prefixValue ? `${this.prefixValue}_${base}` : base
  }
}
